import { Button } from "../../../shared/ui/button";
import { Title } from "../../../shared/ui/title";
import { Auth } from "../auth/auth";
import cx from "classnames";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import {
  useEventMutation,
  useLoginMutation,
  useLoginWithMsisdnMutation,
  useResolveHeMutation,
} from "../../data-access/store/services/auth.service";
import { useEffect, useState } from "react";
import { setMessage } from "../../../shared/data-access/store/message/messageSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  selectInit,
  selectLanguage,
  selectTranslations,
} from "../../../shared/data-access/store/general/generalSlice";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { checkTranslationKey } from "../../../utils/translation";
import {
  tokenReceived,
  updateIsFirstSignIn,
} from "../../data-access/store/authSlice";
import SoundManager from "../../../utils/managers/sound-manager";
import { reset } from "../../../shared/data-access/store/game/gameSlices";
import {
  localsActions,
  LocalsKeysKeys,
  LocalsState,
} from "../../../shared/data-access/store/locals/localsSlice";

import "./sign-in.scss";

export function SignIn() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [login, { isLoading: loginIsLoading, error: loginError }] =
    useLoginMutation();
  const [
    loginWithMsisdn,
    { isLoading: loginWithMsisdnIsLoading, error: loginWithMsisdnError },
  ] = useLoginWithMsisdnMutation();
  const init = useAppSelector(selectInit);
  const lang = useAppSelector(selectLanguage);
  const translations = useAppSelector(selectTranslations);
  const [event] = useEventMutation();
  const location = useLocation();
  const loginForPurchase = location.state === "redirectedFromStore";
  const loginForUnsubscribe = location.state === "redirectedFromProfile";
  const loginType = init?.users?.authType;
  const [prefilledPhone, setPrefilledPhone] = useState("");
  const [resolveHe] = useResolveHeMutation();

  useEffect(() => {
    if (loginError) {
      const errorMsg =
        (loginError as { data?: Error })?.data?.message ||
        checkTranslationKey(translations[lang]?.Error, "Error!");
      if (errorMsg) {
        dispatch(setMessage({ content: errorMsg, type: "error" }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginError]);

  useEffect(() => {
    if (loginWithMsisdnError) {
      const errorMsg =
        (loginWithMsisdnError as { data?: Error })?.data?.message ||
        checkTranslationKey(translations[lang]?.Error, "Error!");
      if (errorMsg) {
        dispatch(setMessage({ content: errorMsg, type: "error" }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginWithMsisdnError]);

  const handleSignInAsGuest = () => {
    dispatch(reset());
    setValue("triggerNewGame", "yes");
    dispatch(tokenReceived(process.env.REACT_APP_GUEST_KEY));
    dispatch(updateIsFirstSignIn(true));
    SoundManager.vfxSounds.play.play();
  };

  const setValue = (key: LocalsKeysKeys, value: LocalsState[typeof key]) => {
    dispatch(localsActions.setValue({ key, value }));
  };

  const prefix = init?.users?.msisdn?.prefix || "";
  const phoneMaxLength = init?.users?.msisdn?.maxLength || 9;
  const phoneMinLength = init?.users?.msisdn?.minLength || 9;
  const pinMaxLength = init?.users?.pin?.maxLength || 6;
  const pinMinLength = init?.users?.pin?.minLength || 4;

  useEffect(() => {
    let cancelled = false;
    resolveHe()
      .unwrap()
      .then((res) => {
        if (cancelled || !res?.msisdn) {
          return;
        }
        const local =
          prefix && res.msisdn.startsWith(prefix)
            ? res.msisdn.slice(prefix.length)
            : res.msisdn;
        setPrefilledPhone(local);
      })
      .catch(() => {
        // HE prefill is optional; ignore when the carrier lookup is unavailable
      });
    return () => {
      cancelled = true;
    };
  }, [resolveHe, prefix]);

  const SignInSchema = Yup.object().shape({
    phone: Yup.string()
      .matches(
        /^[0-9]+$/,
        checkTranslationKey(
          translations[lang]?.The_phone_number_must_be_only_digits,
          "The phone number must be only digits"
        )
      )
      .min(
        phoneMinLength,
        checkTranslationKey(
          translations[lang]?.The_phone_number_is_too_short,
          "The phone number is too short."
        )
      )
      .max(
        phoneMaxLength,
        checkTranslationKey(
          translations[lang]?.The_phone_number_is_too_long,
          "The phone number is too long."
        )
      )
      .required(
        checkTranslationKey(
          translations[lang]?.The_phone_number_is_required,
          "The phone number is required."
        )
      ),
    ...(loginType === "password"
      ? {
          password: Yup.string()
            .matches(
              /^[0-9]+$/,
              checkTranslationKey(
                translations[lang]?.The_password_must_be_only_digits,
                "The password must be only digits"
              )
            )
            .min(
              pinMinLength,
              checkTranslationKey(
                translations[lang]?.The_password_is_too_short,
                "The password is too short."
              )
            )
            .max(
              pinMaxLength,
              checkTranslationKey(
                translations[lang]?.The_password_is_too_long,
                "The password is too long."
              )
            )
            .required(
              checkTranslationKey(
                translations[lang]?.The_password_is_required,
                "The password is required."
              )
            ),
        }
      : {}),
  });

  return (
    <div className="auth-page">
      <Auth
        className="auth-shell-content"
        header={
          <Title>
            {checkTranslationKey(translations[lang]?.Sign_In, "Sign In")}
          </Title>
        }
      >
        <div className="form-title form-title-login">
          {checkTranslationKey(
            translations[lang]?.Enter_your_BattleUp_account,
            "Enter your BattleUp account"
          )}
        </div>
        {loginForPurchase && (
          <div className="notice">
            <span>
              {checkTranslationKey(
                translations[lang]
                  ?.Please_enter_your_credentials_to_proceed_with_the_purchase,
                "Please enter your credentials to proceed with the purchase"
              )}
            </span>
          </div>
        )}
        {loginForUnsubscribe && (
          <div className="notice">
            <span>
              {checkTranslationKey(
                translations[lang]
                  ?.Please_enter_your_credentials_to_proceed_with_the_unsubscribe,
                "Please enter your credentials to proceed with the unsubscribe"
              )}
            </span>
          </div>
        )}
        <div className="form-container">
          <Formik
            enableReinitialize
            initialValues={
              loginType === "password"
                ? { phone: prefilledPhone, password: "" }
                : { phone: prefilledPhone }
            }
            validationSchema={SignInSchema}
            onSubmit={(values) => {
              if (loginType === "password" && values.password) {
                login({
                  msisdn: prefix + values.phone,
                  password: values.password,
                });
              } else if (loginType === "msisdn") {
                loginWithMsisdn({
                  msisdn: prefix + values.phone,
                });
              }
              event({ type: "LOGIN" });
              localStorage.setItem("fromLogin", "true");
            }}
          >
            {({ errors, touched }) => (
              <Form>
                <div
                  className={cx("form-input-row", {
                    danger: errors.phone && touched.phone,
                    success: !errors.phone,
                  })}
                >
                  <div className="prefix-input">
                    <span className="prefix">+{prefix}</span>
                    <Field
                      type="tel"
                      name="phone"
                      className="half-input"
                      placeholder={checkTranslationKey(
                        translations[lang]?.Your_phone_number,
                        "Your phone number"
                      )}
                      autoComplete="off"
                      maxLength={phoneMaxLength}
                      required
                    />
                  </div>
                  <ErrorMessage name="phone">
                    {(msg) => (
                      <div className="error-msg">
                        <img
                          src="/img/icns/alert-fill.svg"
                          alt="alert"
                          className="color-svg"
                        />
                        {msg}
                      </div>
                    )}
                  </ErrorMessage>
                </div>

                {loginType === "password" && (
                  <div
                    className={cx("form-input-row", {
                      danger: errors.password && touched.password,
                    })}
                  >
                    <Field
                      autoComplete="off"
                      className="full-input"
                      type="password"
                      name="password"
                      placeholder={checkTranslationKey(
                        translations[lang]?.Your_password,
                        "Your password"
                      )}
                      maxLength={pinMaxLength}
                    />
                    <ErrorMessage name="password">
                      {(msg) => (
                        <div className="error-msg">
                          <img
                            src="/img/icns/alert-fill.svg"
                            alt="alert"
                            className="color-svg"
                          />
                          {msg}
                        </div>
                      )}
                    </ErrorMessage>
                  </div>
                )}

                <div>
                  <div className="submit">
                    <Button
                      buttonType="success"
                      type="submit"
                      value="Submit"
                      disabled={loginIsLoading || loginWithMsisdnIsLoading}
                    >
                      {loginIsLoading
                        ? checkTranslationKey(
                            translations[lang]?.Signing_in,
                            "Signing in..."
                          )
                        : checkTranslationKey(
                            translations[lang]?.Sign_in,
                            "Sign in"
                          )}
                    </Button>
                  </div>
                  <div className="forgot-area">
                    <Link
                      to="/auth/forgot-password"
                      className="forgot-password"
                    >
                      {checkTranslationKey(
                        translations[lang]?.Forgot_your_password,
                        "Forgot your password?"
                      )}
                    </Link>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Auth>
      {!loginForPurchase
        ? !loginForUnsubscribe && (
            <div className="no-account-area content">
              <div className="no-account">
                {checkTranslationKey(
                  translations[lang]?.Don_t_have_an_account,
                  "Don't have an account?"
                )}
              </div>
              <div className="button-submit">
                <Button
                  onClick={() => navigate("/auth/sign-up")}
                  buttonType="white"
                  small
                >
                  {checkTranslationKey(translations[lang]?.Sign_up, "Sign up")}
                </Button>
                <Button onClick={handleSignInAsGuest} buttonType="white" small>
                  {checkTranslationKey(
                    translations[lang]?.Play_as_guest,
                    "Play as guest"
                  )}
                </Button>
              </div>
            </div>
          )
        : null}
    </div>
  );
}

export default SignIn;
